import React, { useState } from "react";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Link, TextField, Typography } from "@mui/material";
import {
  ArrowBackRounded,
  EditOutlined,
  HelpOutlineRounded,
  LockOutlined,
  NoteAddOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DealEventType,
  DealField,
  DealUser,
  InternalDeal,
  InternalDealEvent,
  RegistrationStatus,
  SalesStatus,
} from "../../../Models/ambassador";
import {
  useDeal,
  useDealOwners,
  useDeleteDealDocument,
  useProcessDeal,
  useUploadDealDocuments,
} from "../../../Query/useAmbassador";
import {
  ErrorState,
  InfoRow,
  LoadingState,
  PageHeader,
  Panel,
  RegistrationStatusChip,
  SalesStatusChip,
} from "../Common/AmbassadorUi";
import DealConversation from "../Common/DealConversation";
import DealTimeline, { TimelineEntry } from "../Common/DealTimeline";
import {
  ambassadorPaths,
  useAmbassadorAccess,
} from "../Common/ambassadorNavigation";
import { displayName, formatDate, formatDateTime } from "../Common/format";
import { useAmbassadorLabels } from "../Common/useAmbassadorLabels";
import DealDataSummary from "../Deals/DealDataSummary";
import { toFormData } from "../Deals/DealForm/dealFormModel";
import CorrectionDialog from "./CorrectionDialog";
import { InternalNotice } from "./DealInbox";
import ProcessingPanel from "./ProcessingPanel";
import DealDocuments from "../Common/DealDocuments";
import { getCurrentUserId } from "../../../Helpers/authHelpers";

const OPEN_STATUSES = [
  RegistrationStatus.SUBMITTED,
  RegistrationStatus.UNDER_REVIEW,
  RegistrationStatus.QUESTIONS_PENDING,
];
// The ambassador can no longer answer on rejected or withdrawn deals.
const CLOSED_STATUSES = [
  RegistrationStatus.REJECTED,
  RegistrationStatus.WITHDRAWN,
];

/** Adds an internal note; only como sees it. */
const NoteForm: React.FC<{ dealId: number }> = ({ dealId }) => {
  const { t } = useTranslation("ambassador");
  const processDeal = useProcessDeal();
  const [note, setNote] = useState("");

  const save = () =>
    processDeal.mutate(
      { dealId, data: { internal_note: note.trim() } },
      { onSuccess: () => setNote("") },
    );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}>
      <TextField
        value={note}
        onChange={(event) => setNote(event.target.value)}
        placeholder={t("review.internalNotePlaceholder")}
        multiline
        minRows={2}
        fullWidth
        inputProps={{ maxLength: 5000, "aria-label": t("review.internalNote") }}
        sx={{ "& .MuiOutlinedInput-root": { backgroundColor: color.yellow50 } }}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<NoteAddOutlined />}
          disabled={processDeal.isPending || !note.trim()}
          onClick={save}
        >
          {t("review.addNote")}
        </Button>
      </Box>
    </Box>
  );
};

/** The full history with values, actors and what stayed internal. */
const useInternalHistory = (
  deal: InternalDeal,
  owners: DealUser[],
): TimelineEntry[] => {
  const { t } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const people = [...owners, ...(deal.owner ? [deal.owner] : [])];

  const detail = (event: InternalDealEvent): string => {
    switch (event.event_type) {
      case DealEventType.SALES_STATUS_CHANGED:
        return t("history.salesChange", {
          from:
            event.old_value === null
              ? "–"
              : labels.salesStatus(event.old_value as SalesStatus),
          to: labels.salesStatus(event.new_value as SalesStatus),
        });
      case DealEventType.OWNER_ASSIGNED: {
        const owner = people.find((user) => user.id === event.new_value);
        return t("history.ownerChange", {
          owner:
            event.new_value === null
              ? t("inbox.unassigned")
              : displayName(owner) || `#${event.new_value}`,
        });
      }
      case DealEventType.DATA_CORRECTED:
        return t("history.correctedFields", {
          fields: Object.keys(
            (event.new_value as Record<string, unknown>) ?? {},
          )
            .map((field) => t(`fields.${field as DealField}`))
            .join(", "),
        });
      default:
        return event.note;
    }
  };

  return deal.history.map((event) => ({
    id: event.id,
    created_at: event.created_at,
    label: labels.event(event.event_type),
    note: detail(event),
    muted: !event.visible_to_ambassador,
    meta: (
      <>
        {displayName(event.actor) || t("history.system")}
        {!event.visible_to_ambassador && (
          <Box
            component="span"
            sx={{
              ml: 0.75,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
            }}
          >
            <LockOutlined sx={{ fontSize: 12 }} />
            {t("history.internalOnly")}
          </Box>
        )}
      </>
    ),
  }));
};

const DealReviewContent: React.FC<{
  deal: InternalDeal;
  owners: DealUser[];
}> = ({ deal, owners }) => {
  const { t, i18n } = useTranslation("ambassador");
  const navigate = useNavigate();
  const { isAdmin } = useAmbassadorAccess();
  const labels = useAmbassadorLabels();
  const [correcting, setCorrecting] = useState(false);
  const history = useInternalHistory(deal, owners);
  const uploadDocuments = useUploadDealDocuments();
  const deleteDocument = useDeleteDealDocument();
  const currentUserId = getCurrentUserId();
  const processDeal = useProcessDeal();
  const canAsk = OPEN_STATUSES.includes(deal.registration_status);

  const sendMessage = (message: string, asQuestion: boolean) =>
    processDeal.mutateAsync({
      dealId: deal.id,
      data: {
        external_message: message,
        ...(asQuestion && {
          registration_status: RegistrationStatus.QUESTIONS_PENDING,
        }),
      },
    });

  return (
    <>
      <PageHeader
        title={t("review.title", { title: deal.title })}
        subtitle={t("review.subtitle", {
          number: deal.deal_number,
          company: deal.company_name,
          date: formatDate(deal.submitted_at, i18n.language),
          ambassador: displayName(deal.ambassador) || "–",
        })}
        actions={
          <>
            {deal.registration_status !== RegistrationStatus.WITHDRAWN && (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<EditOutlined />}
                onClick={() => setCorrecting(true)}
              >
                {t("review.correct")}
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ArrowBackRounded />}
              onClick={() => navigate(ambassadorPaths.inbox)}
            >
              {t("review.backToInbox")}
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.5 }}>
          <RegistrationStatusChip status={deal.registration_status} />
          {deal.sales_status !== null && (
            <SalesStatusChip status={deal.sales_status} />
          )}
        </Box>
      </PageHeader>

      <InternalNotice />

      {deal.possible_duplicates.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
            {t("review.duplicatesTitle", {
              count: deal.possible_duplicates.length,
            })}
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {deal.possible_duplicates.map((duplicate) => (
              <li key={duplicate.id}>
                <Link
                  component={RouterLink}
                  to={ambassadorPaths.review(duplicate.id)}
                  color="inherit"
                >
                  {duplicate.deal_number}
                </Link>
                {" · "}
                {duplicate.company_name} · {duplicate.title} ·{" "}
                {displayName(duplicate.ambassador) || "–"} ·{" "}
                {labels.registrationStatus(duplicate.registration_status)}
              </li>
            ))}
          </Box>
          <Typography sx={{ fontSize: 13, mt: 0.5 }}>
            {t("review.duplicatesHint")}
          </Typography>
        </Alert>
      )}

      {deal.registration_status === RegistrationStatus.REJECTED &&
        deal.rejection_reason && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {t("details.rejectedReason", { reason: deal.rejection_reason })}
          </Alert>
        )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) 400px",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}
        >
          <Panel title={t("review.assignment")}>
            <InfoRow
              label={t("review.ambassador")}
              value={
                deal.ambassador && (
                  <>
                    {displayName(deal.ambassador)}
                    <Typography
                      component="span"
                      sx={{ ml: 1, fontSize: 13, color: color.gray500 }}
                    >
                      {deal.ambassador.email}
                    </Typography>
                  </>
                )
              }
            />
            <InfoRow
              label={t("review.workspace")}
              value={deal.workspace?.name}
            />
            <InfoRow
              label={t("review.owner")}
              value={displayName(deal.owner) || t("inbox.unassigned")}
            />
            <InfoRow
              label={t("review.submittedAt")}
              value={formatDateTime(deal.submitted_at, i18n.language)}
            />
            {deal.decided_at && (
              <InfoRow
                label={t("review.decidedAt")}
                value={formatDateTime(deal.decided_at, i18n.language)}
              />
            )}
          </Panel>

          <Panel title={t("details.dealInformation")}>
            <DealDataSummary data={toFormData(deal)} />
          </Panel>

          <Panel
            title={t("review.conversationTitle")}
            subtitle={t("review.conversationHint")}
          >
            <DealConversation
              messages={deal.messages}
              viewer="como"
              emptyText={t("details.noMessages")}
              onSend={
                CLOSED_STATUSES.includes(deal.registration_status)
                  ? undefined
                  : (message) => sendMessage(message, false)
              }
              sendLabel={t("review.sendMessage")}
              secondaryAction={
                canAsk
                  ? {
                      label: t("review.askQuestion"),
                      icon: <HelpOutlineRounded />,
                      onSend: (message) => sendMessage(message, true),
                    }
                  : undefined
              }
              placeholder={t("review.externalMessagePlaceholder")}
              hint={canAsk ? t("review.askQuestionHint") : undefined}
              pending={processDeal.isPending}
            />
          </Panel>

          <Panel
            title={t("documents.title")}
            subtitle={t("documents.comoHint")}
          >
            <DealDocuments
              side="como"
              dealId={deal.id}
              documents={deal.documents}
              onUpload={(files) =>
                uploadDocuments.mutateAsync({ dealId: deal.id, files })
              }
              onDelete={(documentId) =>
                deleteDocument.mutateAsync({ dealId: deal.id, documentId })
              }
              canDelete={(document) =>
                isAdmin || document.uploaded_by?.id === currentUserId
              }
              pending={uploadDocuments.isPending || deleteDocument.isPending}
            />
          </Panel>

          <Panel
            title={t("review.notesTitle")}
            subtitle={t("review.notesHint")}
          >
            {deal.notes.length === 0 ? (
              <Typography variant="body2">{t("review.noNotes")}</Typography>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {deal.notes.map((note) => (
                  <Box
                    key={note.id}
                    sx={{
                      p: 1.5,
                      borderRadius: "10px",
                      backgroundColor: color.yellow50,
                      border: `1px solid ${color.yellow200}`,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: 12, color: color.gray500, mb: 0.5 }}
                    >
                      {displayName(note.author) || "–"} ·{" "}
                      {formatDateTime(note.created_at, i18n.language)}
                    </Typography>
                    <Typography sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>
                      {note.note}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            <NoteForm dealId={deal.id} />
          </Panel>

          <Panel
            title={t("review.historyTitle")}
            subtitle={t("review.historyHint")}
          >
            <DealTimeline
              entries={history}
              emptyText={t("details.noHistory")}
            />
          </Panel>
        </Box>

        <Box sx={{ position: { lg: "sticky" }, top: { lg: 24 } }}>
          {/* Starts over after a saved decision, not after a message or note. */}
          <ProcessingPanel
            key={[
              deal.registration_status,
              deal.sales_status,
              deal.released_sales_status,
              deal.owner?.id,
            ].join("-")}
            deal={deal}
            owners={owners}
            isAdmin={isAdmin}
          />
        </Box>
      </Box>

      {correcting && (
        <CorrectionDialog deal={deal} onClose={() => setCorrecting(false)} />
      )}
    </>
  );
};

/** como's review of one submitted deal. */
const DealReview: React.FC = () => {
  const { dealId } = useParams();
  const deal = useDeal(Number(dealId));
  const { data: owners = [] } = useDealOwners();

  if (deal.isLoading) return <LoadingState />;
  if (deal.isError || !deal.data)
    return <ErrorState onRetry={() => deal.refetch()} />;
  return <DealReviewContent deal={deal.data} owners={owners} />;
};

export default DealReview;
